import {CRM_PERMISSIONS} from 'src/utils/crm-new/common';
import {selectHasCrmPermission} from 'src/selectors/crm';
import type {MenuOption} from '@spaced-out/ui-design-system/lib/components/Menu';
import {
  size100,
  size180,
} from '@spaced-out/ui-design-system/lib/styles/variables/_size';


export const MILESTONE_TYPE = {
  SCREEN: 'SCREEN',
  OFFER: 'OFFER',
  HIRE: 'HIRE',
  CUSTOM: 'CUSTOM',
} as const;

export const STAGE_TYPE = {
  SCREENING: 'SCREENING',
  OFFERED: 'OFFERED',
  HIRED: 'HIRED',
  GENERAL: 'GENERAL',
  JOINED: 'JOINED',
} as const;

export const JOB_TYPE_TO_ROUTE = {
  OPEN: 'hiring',
  DRAFT: 'draft',
  CLOSED: 'closed',
  ARCHIVED: 'archived',
} as const;

export const TASK_TYPE_TO_ROUTE = {
  GENERATE_OFFER: 'generate-offer',
  OFFER_SENT_TO_CANDIDATE: 'view-offer',
  REVIEW_OFFER: 'review-offer',
  REQUEST_FOR_OFFER_APPROVAL: 'request-offer-approval',
  SEND_OFFER_TO_CANDIDATE: 'send-offer-to-candidate',
  SEND_CANDIDATE_FORM: 'send-candidate-form',
  SEND_INTERNAL_FORM: 'send-internal-form',
  SEND_ASSESSMENT: 'send-assessment',
  SEND_REVIEW_REQUEST: 'send-review-request',
  REQUESTED_FOR_APPROVAL: 'approve-offer',
  SCHEDULE_INTERVIEW: 'schedule-interview',
  REVIEW_REFERRED_SCREEN_APPLICATION: 'review-referred-screen-application',
} as const;

const implementedTasks = [
  'GENERATE_OFFER',
  'REVIEW_OFFER',
  'SEND_OFFER_TO_CANDIDATE',
  'SEND_CANDIDATE_FORM',
  'SEND_INTERNAL_FORM',
  // 'COLLECT_INTERVIEW_FEEDBACK',
  'SEND_ASSESSMENT',
  'SEND_REVIEW_REQUEST',
  'SCHEDULE_INTERVIEW',
  'REVIEW_REFERRED_SCREEN_APPLICATION',
];

export const getTaskTypeSpecificAction = (
  taskType: string,
  userInfo: any,
): string => {
  const taskTypeSpecificAction = {
    GENERATE_OFFER: 'generate-offer',
    // REVIEW_OFFER: 'review-offer',
    SEND_OFFER_TO_CANDIDATE: 'send-offer-to-candidate',
    REQUEST_FOR_OFFER_APPROVAL: 'request-offer-approval',
    SEND_CANDIDATE_FORM: 'send-candidate-form',
    SEND_INTERNAL_FORM: 'send-internal-form',
    SEND_ASSESSMENT: 'send-assessment',
    SEND_REVIEW_REQUEST: 'send-review-request',
    SCHEDULE_INTERVIEW: 'schedule-interview',
    // REVIEW_REFERRED_SCREEN_APPLICATION: 'review-referred-screen-application',
  };

  if (
    !selectHasCrmPermission(
      userInfo,
      CRM_PERMISSIONS.ENABLE_SEND_FOR_APPROVAL_OFFER_LETTER,
    )
  ) {
    // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type 'keyof typeof taskTypeSpecificAction'.
    delete taskTypeSpecificAction.REQUEST_FOR_OFFER_APPROVAL;
  }

  // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type 'keyof typeof taskTypeSpecificAction'.
  return taskTypeSpecificAction[taskType] || '';
};

export const getImplementedTasks = (
  userInfo: any,
  isConversationalCRMFlowEnabled?: boolean,
): string[] => {
  const implementedTasksLocal = [...implementedTasks];
  if (
    selectHasCrmPermission(
      userInfo,
      CRM_PERMISSIONS.ENABLE_SEND_FOR_APPROVAL_OFFER_LETTER,
    )
  ) {
    implementedTasksLocal.push('REQUEST_FOR_OFFER_APPROVAL');
  }
  if (isConversationalCRMFlowEnabled) {
    implementedTasksLocal.push('CANDIDATE_NOT_CONTACTED');
    implementedTasksLocal.push('VOICE_SCREENING_READY');
    implementedTasksLocal.push('COLLECT_REVIEW_FEEDBACK');
  }
  return implementedTasksLocal;
};

export const OFFER_STAGE_TASKS_KEYS = [
  'generate-offer',
  'review-offer',
  'send-offer-to-candidate',
  'request-offer-approval',
  'mark-as-hired',
] as const;

export const OFFER_STAGE_TASKS = [
  'GENERATE_OFFER',
  'REVIEW_OFFER',
  'SEND_OFFER_TO_CANDIDATE',
  'REQUEST_FOR_OFFER_APPROVAL',
  'REQUESTED_FOR_APPROVAL',
  'OFFER_SENT_TO_CANDIDATE',
  'OFFER_ACCEPTED_BY_CANDIDATE',
] as const;

export const getSourcePerformanceData = (
  t: (arg1: string, arg2: string) => string,
): Array<{
  value: string;
  label: string;
}> => [
  {
    value: 'AUTO_SCREENING',
    label: t('Auto_Screen', 'Auto Screen'),
  },
  {value: 'REFERRAL', label: t('Referred', 'Referred')},
  {value: 'CAMPUS', label: t('Campus', 'Campus')},
  {value: 'DIRECT', label: t('Uploaded', 'Uploaded')},
  {value: 'JOB_APPLICATION', label: t('Careers_Page', 'Careers Page')},
  {value: 'JOB_PORTAL', label: t('Job_Portal', 'Job Portal')},
  {value: 'THIRD_PARTY', label: t('External_Agency', 'External Agency')},
  {value: 'SOCIAL_MEDIA', label: t('Social_Media', 'Social Media')},
  {value: 'WEB_EXTENSION', label: t('Web_Extension', 'Web Extension')},
  {value: 'WALK_IN', label: t('Walk_In', 'Walk In')},
  {value: 'ADS', label: t('ADS', 'ADS')},
  {value: 'INTERNAL', label: t('Internal', 'Internal')},
  {value: 'RESUME_MAIL', label: t('Emailed', 'Emailed')},
  {value: 'MAIL_SYNC', label: t('Mail_Synced', 'Mail Synced')},
  {
    value: 'IMPORT_SPREADSHEET',
    label: t('Imported_from_Spreadsheet', 'Imported from Spreadsheet'),
  },
  {
    value: 'ADDED_FROM_ANOTHER_JOB',
    label: t('Added_from_another_job', 'Added from another job'),
  },
  {
    value: 'IMPORT_FROM_DRIVE',
    label: t('Imported_from_Drive', 'Imported from Drive'),
  },
  {value: 'EVENT', label: t('Event', 'Event')},
];

export const getTasks = (
  t: (arg1: string, arg2: string) => string,
): {
  [key: string]: {
    label: string;
    iconName: string;
    btnText: string;
    sortOrder?: number;
  };
} => ({
  GENERATE_OFFER: {
    label: t('Offer_to_be_Created', 'Offer to be Created'),
    iconName: 'clipboard-question',
    btnText: t('Create_Offer', 'Create Offer'),
    sortOrder: 1,
  },
  REQUEST_FOR_OFFER_APPROVAL: {
    label: t('Offer_to_be_sent_for_Approval', 'Offer to be sent for Approval'),
    iconName: 'send',
    btnText: t('Review_and_Send', 'Review and Send'),
    sortOrder: 2,
  },
  SEND_OFFER_TO_CANDIDATE: {
    label: t('Offer_ready_to_be_rolled_out', 'Offer ready to be rolled out'),
    iconName: 'file-check',
    btnText: t('Review_and_Send', 'Review and Send'),
    sortOrder: 3,
  },
  SCHEDULE_INTERVIEW: {
    label: t('Interview_to_be_scheduled', 'Interview to be scheduled'),
    iconName: 'calendar',
    btnText: t('Schedule_Interview', 'Schedule Interview'),
    sortOrder: 5,
  },
  // COLLECT_INTERVIEW_FEEDBACK: {
  //   label: 'Feedback to be requested',
  //   iconName: 'message',
  //   btnText: 'Request Feedback',
  //   sortOrder: 7,
  // },
  REQUESTED_FOR_APPROVAL: {
    label: t('Offer_to_approve', 'Offer to approve'),
    iconName: 'check-circle',
    btnText: t('Review_and_Approve', 'Review and Approve'),
    sortOrder: 11,
  },
  SEND_CANDIDATE_FORM: {
    label: t('Candidate_Form_to_send', 'Candidate Form to send'),
    iconName: 'file',
    btnText: t('Send', 'Send'),
    sortOrder: 13,
  },
  SEND_INTERNAL_FORM: {
    label: t('Internal_Form_to_send', 'Internal Form to send'),
    iconName: 'file',
    btnText: t('Send', 'Send'),
    sortOrder: 14,
  },
  SEND_ASSESSMENT: {
    label: t('Assessment_to_send', 'Assessment to send'),
    iconName: 'file',
    btnText: t('Send', 'Send'),
    sortOrder: 15,
  },
  SEND_REVIEW_REQUEST: {
    label: t('Feedback_to_be_collected', 'Feedback to be collected'),
    iconName: 'message',
    btnText: t('Collect_Feedback', 'Collect Feedback'),
    sortOrder: 16,
  },
  REQUESTED_FOR_CANDIDATE_INFO: {
    label: t('Requested_for_Candidate_Info', 'Requested for Candidate Info'),
    iconName: 'message',
    btnText: t('Requested_Feedback', 'Requested Feedback'),
    sortOrder: 17,
  },
  MOVE_NEXT_STAGE: {
    label: t('Move_to_Next_Stage', 'Move to Next Stage'),
    iconName: 'arrow-right',
    btnText: t('Move_to_Next_Stage', 'Move to Next Stage'),
  },
  OFFER_ACCEPTED_BY_CANDIDATE: {
    label: t('Offer_Accepted_by_Candidate', 'Offer Accepted by Candidate'),
    iconName: 'check-circle',
    btnText: t('Accept_Offer', 'Accept Offer'),
  },
  COMPLETE: {
    label: t('Offer_Completed', 'Offer Completed'),
    iconName: 'check-circle',
    btnText: t('View_Offer', 'View Offer'),
  },
  COMPLETED: {
    label: t('Offer_Completed', 'Offer Completed'),
    iconName: 'check-circle',
    btnText: t('View_Offer', 'View Offer'),
  },
  OFFER_SENT_TO_CANDIDATE: {
    label: t('Offer_Sent_to_Candidate', 'Offer Sent to Candidate'),
    iconName: 'send',
    btnText: t('View_Offer', 'View Offer'),
  },
  INTERVIEW_CANCELLED: {
    label: t('Interview_Cancelled', 'Interview Cancelled'),
    iconName: 'xmark',
    btnText: t('Review_and_Cancel', 'Review and Cancel'),
  },
  REVIEW_REFERRED_SCREEN_APPLICATION: {
    label: 'Review Referred Screen Application',
    iconName: 'message',
    btnText: 'Review Application',
  },
});

export const DASHBOARD_STATUS_TYPE = [
  'OFFER_SENT_TO_CANDIDATE',
  'REQUESTED_FOR_APPROVAL',
  'GENERATE_OFFER',
  'SCHEDULE_INTERVIEW',
  // 'COLLECT_INTERVIEW_FEEDBACK',
  'SEND_REVIEW_REQUEST',
  'SEND_ASSESSMENT',
  'SEND_CANDIDATE_FORM',
  'SEND_INTERNAL_FORM',
] as const;

export const getDashboardTasks = (
  t: (arg1: string, arg2: string) => string,
  isConversationalCrmFlowEnabled?: boolean,
  userInfo?: any,
): {
  [key: string]: {
    label?: string;
    iconName?: string;
    sortOrder?: number;
  };
} => {
  const dashboardTasks = {
    ...(isConversationalCrmFlowEnabled
      ? {
          CANDIDATE_NOT_CONTACTED: {
            label: t('Candidate_Not_Contacted', 'Candidate Not Contacted'),
            iconName: 'message',
            sortOrder: 0,
          },
        }
      : {}),
    ...(isConversationalCrmFlowEnabled
      ? {
          VOICE_SCREENING_READY: {
            label: t(
              'Voice_Screenings_Ready_for_Review',
              'Voice Screenings Ready for Review',
            ),
            iconName: 'clipboard-question',
            sortOrder: 0,
          },
        }
      : {}),
    SEND_OFFER_TO_CANDIDATE: {
      label: t('Offer_ready_to_be_rolled_out', 'Offer ready to be rolled out'),
      iconName: 'file-check',
      sortOrder: 1,
    },
    REQUEST_FOR_OFFER_APPROVAL: {
      label: t(
        'Offer_to_be_sent_for_Approval',
        'Offer to be sent for Approval',
      ),
      iconName: 'send',
      sortOrder: 1,
    },
    GENERATE_OFFER: {
      label: t('Offer_to_be_Created', 'Offer to be Created'),
      iconName: 'file-check',
      sortOrder: 1,
    },
    SCHEDULE_INTERVIEW: {
      label: t('Interview_to_be_scheduled', 'Interview to be scheduled'),
      iconName: 'calendar',
      sortOrder: 4,
    },
    // COLLECT_INTERVIEW_FEEDBACK: {
    //   label: 'Interview Feedback pending',
    //   iconName: 'message',
    //   sortOrder: 5,
    // },
    COLLECT_REVIEW_FEEDBACK: {
      label: t('Feedback_to_be_Collected', 'Feedback to be Collected'),
      iconName: 'message',
      sortOrder: 6,
    },
    REQUESTED_FOR_APPROVAL: {
      label: t('Offers_awaiting_approval', 'Offers awaiting approval'),
      iconName: 'paper-plane-top',
      sortOrder: 2,
    },
    SEND_CANDIDATE_FORM: {
      label: t('Candidate_Form_to_send', 'Candidate Form to send'),
      iconName: 'file-signature',
      sortOrder: 9,
    },
    OFFER_SENT_TO_CANDIDATE: {
      label: t('Offer_sent_to_candidate', 'Offer sent to candidate'),
      iconName: 'clipboard-question',
      sortOrder: 3,
    },
    OFFER_ACCEPTED_BY_CANDIDATE: {
      label: t('Mark_candidate_as_hired', 'Mark candidate as hired'),
      iconName: 'user-check',
      sortOrder: 3,
    },
    SEND_REVIEW_REQUEST: {
      label: t('Applicants_to_Review', 'Applicants to Review'),
      iconName: 'users',
      sortOrder: 6,
    },
    SEND_INTERNAL_FORM: {
      label: t('Internal_Form_To_Send', 'Internal Form To Send'),
      iconName: 'file-alt',
      sortOrder: 10,
    },
    SEND_ASSESSMENT: {
      label: t('Assessments_to_Send', 'Assessments to Send'),
      iconName: 'file-pen',
      sortOrder: 8,
    },
    MOVE_NEXT_STAGE: {
      label: t('Applicants_to_Decide_On', 'Applicants to Decide On'),
      iconName: 'user-clock',
      sortOrder: 7,
    },
    REVIEW_REFERRED_SCREEN_APPLICATION: {
      label: t(
        'Review_Referred_Screen_Application',
        'Review Referred Screen Application',
      ),
      iconName: 'message',
      sortOrder: 11,
    },
  };

  if (isConversationalCrmFlowEnabled) {
    Object.keys(dashboardTasks).filter((taskKey) =>
      getImplementedTasks(userInfo, isConversationalCrmFlowEnabled).includes(
        taskKey,
      ),
    );
  }
  return dashboardTasks;
};

export const getFeedbackTabs = (
  t: (arg1: string, arg2: string) => string,
): Array<{
  label?: string;
  tabId: string;
}> => [
  {
    label: t('Interview_Feedback', 'Interview Feedback'),
    tabId: 'interviewFeedbacks',
  },
  {
    label: t('Application_Review', 'Application Review'),
    tabId: 'applicationReview',
  },
  {
    label: t('Survey', 'Survey'),
    tabId: 'survey',
  },
  {
    label: t('Completed', 'Completed'),
    tabId: 'completed',
  },
];

export const getSubmitFeedbackTabs = (
  t: (arg1: string, arg2: string) => string,
): Array<{
  label?: string;
  tabId: string;
  maxWidth?: string;
}> => [
  {
    label: t('Feedback', 'Feedback'),
    tabId: 'FEEDBACK',
    maxWidth: size100,
  },
  {
    label: t('Candidate_Details', 'Candidate Details'),
    tabId: 'CANDIDATE_DETAILS',
    maxWidth: size180,
  },
  {
    label: t('Resume', 'Resume'),
    tabId: 'RESUME',
    maxWidth: size100,
  },
  {
    label: t('Past_Reviews', 'Past Reviews'),
    tabId: 'PAST_REVIEWS',
    maxWidth: size100,
  },
  {
    label: t('Job_Details', 'Job Details'),
    tabId: 'BASIC_JOB_DETAILS',
    maxWidth: size100,
  },
];

export const JOB_CANDIDATE_STATUS = {
  SCREEN: 'SCREEN',
  SCREEN_ONHOLD: 'SCREEN_ONHOLD',
  SCREEN_REJECTED: 'SCREEN_REJECTED',
  INTERVIEW: 'INTERVIEW',
  INTERVIEW_INACTIVE: 'INTERVIEW_INACTIVE',
  INTERVIEW_ONHOLD: 'INTERVIEW_ONHOLD',
  INTERVIEW_REJECTED: 'INTERVIEW_REJECTED',
  OFFER: 'OFFER',
  OFFER_ONHOLD: 'OFFER_ONHOLD',
  OFFER_REJECTED: 'OFFER_REJECTED',
  HIRE: 'HIRE',
  HIRE_ONHOLD: 'HIRE_ONHOLD',
  HIRE_REJECTED: 'HIRE_REJECTED',
  JOINED: 'JOINED',
  NOT_ADDED: 'NOT_ADDED',
} as const;

export const activeApplicationStatuses = [
  JOB_CANDIDATE_STATUS.INTERVIEW,
  JOB_CANDIDATE_STATUS.OFFER,
  JOB_CANDIDATE_STATUS.HIRE,
  JOB_CANDIDATE_STATUS.JOINED,
  JOB_CANDIDATE_STATUS.INTERVIEW_ONHOLD,
  JOB_CANDIDATE_STATUS.OFFER_ONHOLD,
  JOB_CANDIDATE_STATUS.HIRE_ONHOLD,
  'SHORTLISTED',
] as const;

export const getSources = (
  t: (arg1: string, arg2: string) => string,
): Array<{
  value: string;
  label: string;
  // @ts-expect-error - TS2300 - Duplicate identifier 'subsourceDropdown'. | TS2687 - All declarations of 'subsourceDropdown' must have identical modifiers.
  subsourceDropdown: string;
  mediums?: Array<{
    value: string;
    label: string;
  }>;
  // @ts-expect-error - TS2300 - Duplicate identifier 'subsourceDropdown'. | TS2687 - All declarations of 'subsourceDropdown' must have identical modifiers. | TS2717 - Subsequent property declarations must have the same type.  Property 'subsourceDropdown' must be of type 'string', but here has type 'string | undefined'.
  subsourceDropdown?: string;
}> => [
  {value: '', label: t('All', 'All')},
  {
    value: 'DIRECT',
    label: t('Uploaded', 'Uploaded'),
    subsourceDropdown: 'USER',
  },
  {
    value: 'INTERNAL',
    label: t('Internal', 'Internal'),
    subsourceDropdown: 'USER',
  },
  {
    value: 'AUTO_SCREENING',
    label: t('Auto_Screen_Candidates', 'Auto Screen Candidates'),
    subsourceDropdown: 'USER',
  },
  {
    value: 'JOB_APPLICATION',
    label: t('Careers_Page', 'Careers Page'),
  },
  {
    value: 'REFERRAL',
    label: t('Referred', 'Referred'),
    subsourceDropdown: 'USER',
    mediums: [
      {value: 'REFERRAL', label: t('Direct', 'Direct')},
      {value: 'DIRECT', label: t('Uploaded', 'Uploaded')},
      {
        value: 'REFERRAL_LINK',
        label: t('Referral_Link', 'Referral Link'),
      },
    ],
  },
  {
    value: 'RESUME_MAIL',
    label: t('Resume_Mailbox', 'Resume Mailbox'),
    subsourceDropdown: 'SEARCHABLE',
  },
  {
    value: 'THIRD_PARTY',
    label: t('External_Agency', 'External Agency'),
    subsourceDropdown: 'AGENCY',
    mediums: [
      {value: 'THIRD_PARTY', label: t('Direct', 'Direct')},
      {value: 'DIRECT', label: t('Uploaded', 'Uploaded')},
    ],
  },
  {
    value: 'MAIL_SYNC',
    label: t('Synced_From_Email', 'Synced From Email'),
    subsourceDropdown: 'LINKED_EMAIL',
  },
  {
    value: 'IMPORT_SPREADSHEET',
    label: t('Imported_from_Spreadsheet', 'Imported from Spreadsheet'),
    subsourceDropdown: 'USER',
  },
  {
    value: 'CIRCA',
    label: t('Circa', 'Circa'),
  },
  {
    value: 'JOB_PORTAL',
    label: t('Job_Portal', 'Job Portal'),
    subsourceDropdown: 'JOB_PORTAL',
    mediums: [
      {value: 'JOB_PORTAL', label: t('Direct', 'Direct')},
      {value: 'DIRECT', label: t('Uploaded', 'Uploaded')},
      {
        value: 'JOB_APPLICATION',
        label: t('Careers_Page', 'Careers Page'),
      },
      {
        value: 'WEB_EXTENSION',
        label: t('Web_Extension', 'Web Extension'),
      },
      {
        value: 'RESUME_MAIL',
        label: t('Resume_Mailbox', 'Resume Mailbox'),
      },
    ],
  },
  {
    value: 'SOCIAL_MEDIA',
    label: t('Social_Media', 'Social Media'),
    subsourceDropdown: 'SOCIAL_MEDIA',
    mediums: [
      {value: 'SOCIAL_MEDIA', label: t('Direct', 'Direct')},
      {
        value: 'RESUME_MAIL',
        label: t('Resume_Mailbox', 'Resume Mailbox'),
      },
      {value: 'DIRECT', label: t('Uploaded', 'Uploaded')},
      {
        value: 'JOB_APPLICATION',
        label: t('Careers_Page', 'Careers Page'),
      },
      {
        value: 'WEB_EXTENSION',
        label: t('Web_Extension', 'Web Extension'),
      },
    ],
  },
  {
    value: 'CAMPUS',
    label: t('Campus_Hiring', 'Campus Hiring'),
    subsourceDropdown: 'SEARCHABLE',
  },
  {
    value: 'WALK_IN',
    label: t('Walk_in', 'Walk in'),
    subsourceDropdown: 'SEARCHABLE',
  },
  {value: 'ADS', label: t('ADS', 'ADS'), subsourceDropdown: 'SEARCHABLE'},
  {
    value: 'ADDED_FROM_ANOTHER_JOB',
    label: t('Added_from_another_job', 'Added from another job'),
    subsourceDropdown: 'USER',
  },
  {
    value: 'TALEO',
    label: t('Taleo', 'Taleo'),
    subsourceDropdown: 'SEARCHABLE',
  },
  {
    value: 'EVENT',
    label: t('Event', 'Event'),
    subsourceDropdown: 'SEARCHABLE',
  },
  {
    value: 'API_INTEGRATION',
    label: t('API_Integration', 'API Integration'),
    subsourceDropdown: 'SEARCHABLE',
  },
];

export const getSubsources = (
  t: (arg1: string, arg2: string) => string,
): {
  [key: string]: Array<MenuOption>;
} => ({
  JOB_APPLICATION: [
    {
      label: t('Direct', 'Direct'),
      key: 'DIRECT',
    },
    {
      label: t('Talent_Pool', 'Talent Pool'),
      key: 'TALENT_POOL',
    },
  ],
  JOB_PORTAL: [
    {
      label: t('Naukri', 'Naukri'),
      key: 'NAUKRI',
    },
    {
      label: t('IIM_Jobs', 'IIM Jobs'),
      key: 'IIM_JOBS',
    },
    {
      label: t('Times_Jobs', 'Times Jobs'),
      key: 'TIMES_JOBS',
    },
    {
      label: t('LinkedIn', 'LinkedIn'),
      key: 'LINKEDIN',
    },
    {
      label: t('Monster', 'Monster'),
      key: 'MONSTER',
    },
    {
      label: t('Indeed', 'Indeed'),
      key: 'INDEED',
    },
    {
      label: t('Broadbean', 'Broadbean'),
      key: 'BROADBEAN',
    },
    {
      label: t('Shine_Jobs', 'Shine Jobs'),
      key: 'SHINE',
    },
    {
      label: t('AngelList', 'AngelList'),
      key: 'ANGELLIST',
    },
    {
      label: t('Instahyre', 'Instahyre'),
      key: 'INSTAHYRE',
    },
    {
      label: t('Hirist', 'Hirist'),
      key: 'HIRIST',
    },
    {
      label: t('Glassdoor', 'Glassdoor'),
      key: 'GLASSDOOR',
    },
    {
      label: t('Internshala', 'Internshala'),
      key: 'INTERNSHALA',
    },
    {
      label: t('Upgrad', 'Upgrad'),
      key: 'UPGRAD',
    },
    {
      label: t('Others', 'Others'),
      key: 'OTHERS',
    },
    {
      label: t('Naukri_Resdex', 'Naukri Resdex'),
      key: 'NAUKRI_RESDEX',
    },
    {
      label: t('Apna', 'Apna'),
      key: 'APNA',
    },
    {
      label: t('Cutshort', 'Cutshort'),
      key: 'CUTSHORT',
    },
    {
      label: t('JobStreet', 'JobStreet'),
      key: 'JOBSTREET',
    },
  ],
  SOCIAL_MEDIA: [
    {
      label: t('LinkedIn', 'LinkedIn'),
      key: 'LINKEDIN',
    },
    {
      label: t('Facebook', 'Facebook'),
      key: 'FACEBOOK',
    },
    {
      label: t('Twitter', 'Twitter'),
      key: 'TWITTER',
    },
    {
      label: t('WhatsApp', 'WhatsApp'),
      key: 'WHATS_APP',
    },
    {
      label: t('Instagram', 'Instagram'),
      key: 'INSTAGRAM',
    },
  ],
});

export const REDIRECT_URL = {
  INTERNAL_JOB_POSTING: '/trm#/internal-job-board',
  SCORECARD_CONFIGURATION: '/trm#/settings/scorecard/sections-and-attributes',
  EMAIL_AUTOMATION: '/trm#/settings/email-automation',
} as const;

export const INITIAL_APPLICANT_LIST_FILTER_DATA = {
  type: 'applied',
  sort: 'overall',
  sortBy: 'OVERALL_SCORE',
  sortOrder: 'DESC',
  filter: {
    // Arush: change it later
    addedToJobTypes: [],
    notAddedToJobTypes: [],
    chatbotScreening: false,
    chatInvite: [],
    source: '',
    medium: '',
    chatbotQualified: [],
    experienceStart: null,
    experienceEnd: null,
    aiStatus: '',
    statuses: ['SCREEN', 'INTERVIEW', 'OFFER', 'HIRE'],
    substatus: '',
    keySkills: [],
    locations: [],
    address: [],
    colleges: [],
    majors: [],
    degrees: [],
    industries: [],
    currentCompanies: [],
    companies: [],
    keywords: [],
    dateRange: null,
    skillsBool: false,
    booleanSkills: '',
    keywordsBool: false,
    booleanKeywords: '',
    activeLooking: false,
    tagIds: [],
    tags: [],
    includedTagIds: [],
    excludedTagIds: [],
    excludeKeywords: [],
    excludeSkills: [],
    excludeCompanies: [],
    resumeCategory: '',
    currentCtcStart: null,
    currentCtcEnd: null,
    expectedCtcStart: null,
    expectedCtcEnd: null,
    currentCtcCurrency: '',
    expectedCtcCurrency: '',
    noticePeriod: null,
    noticePeriodStart: null,
    noticePeriodEnd: null,
    preferredLocations: [],
    names: [],
    emails: [],
    phones: [],
    candidateIds: '',
    q: [],
    gender: [],
    autoScreenFilter: {
      isSameLocation: 'ALL',
      isPreviousEmployee: 'ALL',
      hasWorkedInIndustry: 'ALL',
      prevSameLocation: 'ALL',
      workInShift: 'ALL',
      completed: 'ALL',
      qualifications: [],
      currentLocation: [],
      certification: 'ALL',
      locationPreference: [],
      noticePeriods: [],
      realEstateExperience: 'ALL',
      leadGenerationExperience: 'ALL',
      salesExperience: 'ALL',
      languageExperience: [],
      gender: [],
      hasPreviousApplied: 'ALL',
      activeApplicationsIjp: 'ALL',
    },
    stageIds: [],
    excludedStageIds: [],
    stepIds: [],
    excludedStepIds: [],
    stepStatuses: [],
    excludedStepStatuses: [],
    pgGradeStart: null,
    pgGradeEnd: null,
    ugGradeStart: null,
    ugGradeEnd: null,
    includedTalentPoolIds: [],
    excludedTalentPoolIds: [],
  },
  weightage: null,
  pageSize: 10,
  page: 0,
} as const;

export const NOTIFICATION_ICON = {
  'New Feedback Submitted': 'note', //correct
  'New IJP Submission': 'megaphone', //correct
  'Job approval request APPROVED': 'briefcase', //correct
  'Job approval request REJECTED': 'briefcase', //correct
  'Offer Letter approved': 'party-horn', //correct
  'Offer Letter rejected': 'circle-xmark', //correct
  'Candidate replied to chatbot': 'message-bot', //correct
  'You are assigned a new job': 'briefcase', //correct
  'You are unassigned from a job': 'briefcase', //correct
  'You are assigned new jobs': 'briefcase', //correct
  'Duplicate candidate': 'copy', //correct
  'Referral Submitted Successfully': 'bell', //correct
  'Candidate Application form submitted successfully': 'file-signature', //correct
  'Candidate Status Changed': 'right-left', //correct
  'Candidate Accepted Offer letter': 'check-circle', //correct
  'Candidate Declined Offer letter': 'xmark', //correct
  'New candidate added': 'user-plus', //correct
  'Candidate uploaded attachment': 'file-upload', //correct
  'Form filled on candidate behalf': 'file-signature', //correct
  'New job Assigned': 'briefcase', //correct
  'Job created successfully': 'briefcase', //correct
  'Duplicate resume': 'copy', //correct
  'New Agency Referral': 'user-plus', //correct
  'A job is put on hold': 'briefcase', //correct
  'A job is cancelled': 'briefcase', //correct
  'A job is closed': 'briefcase', //correct
  'Candidate Form Rejected': 'xmark', //correct
  'Bulk Resumes Uploaded': 'file-signature', //correct
  'New Employee Referral': 'user-plus', //correct
  'Candidate Note Tagged': 'quote-left', //correct
  'Job Note Tagged': 'quote-left', //correct
  'Email failed': 'xmark', //correct
  'SMS failed': 'xmark', //correct
  'Offer letter successfully generated': 'file-check', //correct
} as const;

export const integrationTypes = {
  SUCCESSFACTORS: 'SUCCESSFACTORS',
  NONE: 'NONE',
} as const;

const HRMSIntegration = Object.freeze({
  GREYTHR: {label: 'GreytHR', value: 'GREYTHR'},
  PEOPLEWORKS: {
    label: 'People works',
    value: 'PEOPLEWORKS',
  },
  DARWINBOX: {
    label: 'Darwinbox',
    value: 'DARWINBOX',
  },
  DARWINBOXV2: {
    label: 'Darwinbox',
    value: 'DARWINBOXV2',
  },
  ZOHO: {label: 'Zoho', value: 'ZOHO'},
  LEENA: {label: 'Leena', value: 'LEENA'},
  EMAIL_RECRUITER: {
    label: 'Email',
    value: 'EMAIL_RECRUITER',
  },
  SFTP: {label: 'Sftp', value: 'SFTP'},
  SPARSH: {label: 'Sparsh', value: 'SPARSH'},
  API_INTEGRATION: {
    label: 'API Integration',
    value: 'API_INTEGRATION',
  },
  ZINGHR: {label: 'Zing Hr', value: 'ZINGHR'},
  HRMS_DATA_DOWNLOAD: {
    label: '.zip',
    value: 'HRMS_DATA_DOWNLOAD',
  },
  ONGRID: {label: 'Ongrid', value: 'ONGRID', type: 'BACKGROUND_VERIFICATION'},
  ONGRIDV2: {
    label: 'Ongrid V2',
    value: 'ONGRIDV2',
    type: 'BACKGROUND_VERIFICATION',
  },
  AUTH_BRIDGE: {label: 'Auth Bridge', value: 'AUTH_BRIDGE'},
  SPRINGVERIFY: {label: 'Springverify', value: 'SPRINGVERIFY'},

  SF_EC_MODULE: {
    label: 'Sf EC Module',
    value: 'SF_EC_MODULE',
  },
  PEOPLEWORKSV2: {label: 'Peopleworks', value: 'PEOPLEWORKSV2'},
  HIBOB: {
    label: 'Hibob',
    value: 'HIBOB',
  },
  CUSTOM_VARROC: {label: 'Varroc', value: 'CUSTOM_VARROC'},
  KEKA: {
    label: 'Keka',
    value: 'KEKA',
  },
  SFONBOARDINGV2: {label: 'SF Onboarding 2.0', value: 'SFONBOARDINGV2'},
  WORKDAY: {label: 'Workday', value: 'WORKDAY'},
  NONE: {label: 'None', value: 'NONE'},
  SFTPV2: {label: 'SftpV2', value: 'SFTPV2'},
  SFONBOARDING_V1: {label: 'SFONBOARDING_V1', value: 'SFONBOARDING_V1'},
});

export const getWorkplaceTypes = (
  t: (arg1: string, arg2: string) => string,
): Array<{
  key: string;
  label: string;
}> => [
  {
    key: 'ONSITE',
    label: t('On_Site', 'On Site'),
  },
  {
    key: 'REMOTE',
    label: t('Remote', 'Remote'),
  },
  {
    key: 'HYBRID',
    label: t('Hybrid', 'Hybrid'),
  },
  {
    key: 'TEMPORARILY_REMOTE',
    label: t('Temporarily_Remote', 'Temporarily Remote'),
  },
];

export const getBulkActionObject = (
  t: (arg1: string, arg2: string) => string,
) => ({
  CHANGE_STAGE: {
    successMessage: t(
      'Candidates_stage_changed_successfully',
      'Candidates stage changed successfully',
    ),
    errorMessage: t('Unable_to_change_stage', 'Unable to change stage'),
  },
  ADD_TO_JOB: {
    successMessage: t(
      'Candidates_added_to_job_successfully',
      'Candidates added to job successfully',
    ),
    errorMessage: t(
      'Unable_to_add_candidates_to_job',
      'Unable to add candidates to job',
    ),
  },
  ADD_TO_CURRENT_JOB: {
    successMessage: t(
      'Candidates_added_to_job_successfully',
      'Candidates added to job successfully',
    ),
    errorMessage: t(
      'Unable_to_add_candidates_to_job',
      'Unable to add candidates to job',
    ),
  },
  REJECT: {
    successMessage: t(
      'Candidates_rejected_successfully',
      'Candidates rejected successfully',
    ),
    errorMessage: t(
      'Unable_to_reject_candidates',
      'Unable to reject candidates',
    ),
  },
  PUT_ON_HOLD: {
    successMessage: t(
      'Candidates_put_on_hold_successfully',
      'Candidates put on hold successfully',
    ),
    errorMessage: t(
      'Unable_to_put_candidates_on_hold',
      'Unable to put candidates on hold',
    ),
  },
  MOVE_TO_JOB: {
    successMessage: t(
      'Candidates_moved_to_job_successfully',
      'Candidates moved to job successfully',
    ),
    errorMessage: t(
      'Unable_to_move_candidates_to_job',
      'Unable to move candidates to job',
    ),
  },
});

export const getApplicationStatus = (status: string) => {
  const isRejected = [
    'SCREEN_REJECTED',
    'INTERVIEW_REJECTED',
    'OFFER_REJECTED',
    'HIRE_REJECTED',
  ].includes(status);

  const isOnHold = [
    'SCREEN_ONHOLD',
    'INTERVIEW_ONHOLD',
    'OFFER_ONHOLD',
    'HIRE_ONHOLD',
  ].includes(status);

  const isInActive = [
    'INTERVIEW_INACTIVE',
    'SCREEN_INACTIVE',
    'HIRE_INACTIVE',
  ].includes(status);

  return {isRejected, isOnHold, isInActive};
};
